import { create } from 'zustand';
import { i18n } from '@/shared/i18n';

export type PersonaRunStatus = 'pending' | 'running' | 'done';
export type ReportStatus = 'pending' | 'running' | 'done';

export interface PersonaProgress {
  personaId: string;
  personaName: string;
  status: PersonaRunStatus;
  currentQuestion: number;
  totalQuestions: number;
}

interface FocusGroupRunState {
  focusGroupId: string | null;
  personas: PersonaProgress[];
  reportStatus: ReportStatus;
  isRunning: boolean;
  error: string | null;

  initRun: (personaIds: string[], personaNames: Record<string, string>) => void;
  setFocusGroupId: (id: string) => void;
  startPersona: (personaId: string, personaName: string, totalPersonas: number) => void;
  updatePersonaQuestion: (personaId: string, questionIndex: number, totalQuestions: number) => void;
  completePersona: (personaId: string) => void;
  setReportStatus: (status: ReportStatus) => void;
  setError: (message: string) => void;
  setComplete: (focusGroupId: string) => void;
  reset: () => void;
}

const initialState = () => ({
  focusGroupId: null as string | null,
  personas: [] as PersonaProgress[],
  reportStatus: 'pending' as ReportStatus,
  isRunning: false,
  error: null as string | null,
});

export const useFocusGroupRunStore = create<FocusGroupRunState>((set) => ({
  ...initialState(),

  initRun: (personaIds, personaNames) =>
    set({
      ...initialState(),
      isRunning: true,
      personas: personaIds.map((id) => ({
        personaId: id,
        personaName:
          personaNames[id] ?? i18n.t('audiences:focusGroup.useRunFocusGroup.participant'),
        status: 'pending' as const,
        currentQuestion: 0,
        totalQuestions: 0,
      })),
    }),

  setFocusGroupId: (id) => set({ focusGroupId: id }),

  startPersona: (personaId) =>
    set((state) => ({
      personas: state.personas.map((p) =>
        p.personaId === personaId ? { ...p, status: 'running' as const } : p,
      ),
    })),

  updatePersonaQuestion: (personaId, questionIndex, totalQuestions) =>
    set((state) => ({
      personas: state.personas.map((p) =>
        p.personaId === personaId
          ? { ...p, currentQuestion: questionIndex + 1, totalQuestions }
          : p,
      ),
    })),

  completePersona: (personaId) =>
    set((state) => ({
      personas: state.personas.map((p) =>
        p.personaId === personaId ? { ...p, status: 'done' as const } : p,
      ),
    })),

  setReportStatus: (reportStatus) => set({ reportStatus }),

  setError: (message) => set({ error: message, isRunning: false }),

  setComplete: (focusGroupId) => set({ focusGroupId, isRunning: false, reportStatus: 'done' }),

  reset: () => set(initialState()),
}));
